import { type Arguments, type Transform, isIterable, isNullish, isObject, isString, mapArray, notOptional } from "shelving";

/**
 * Is an unknown value a React element?
 * - Looks for an object with a `.type` prop that's a string (e.g. `"div"`), function (e.g. `MyComponent()`), or symbol (e.g. `Symbol(react.suspense)`).
 */
export const isElement = (v: unknown): v is React.ReactElement => isObject(v) && (typeof v.type === "string" || typeof v.type === "function" || typeof v.type === "symbol");

/**
 * Transform every node of a `ReactNode` (recursively).
 *
 * @param input The input node.
 * @param transform Function that transforms every node found in `input`
 * - Called for every leaf node of the `ReactNode` (i.e. every element and node).
 * - If `transform()` returns a different/changed object, we don't recursively iterate into that new object (assume `transform()` did this if it was needed).
 * @returns The transformed node, or the same exact node if no changes were made.
 */
export function transformNodes<A extends Arguments = []>(input: React.ReactNode, transform: Transform<React.ReactNode, React.ReactNode, A>, ...args: A): React.ReactNode {
	const output = transform(input, ...args);
	if (output !== input) return output;
	if (isElement(input)) {
		if (isObject(input.props)) {
			const inputChildren = input.props.children as React.ReactNode;
			const outputChildren = transformNodes(inputChildren, transform, ...args);
			if (inputChildren !== outputChildren) return { ...input, props: { ...input.props, children: outputChildren } };
		}
		return input;
	} else if (isIterable(input) && !isString(input)) {
		return mapArray(input, transformNodes, transform, ...args);
	}
	return input;
}

/**
 * Transform the children of an element (recursively).
 */
export function transformChildren<A extends Arguments = []>(el: React.ReactElement, transform: Transform<React.ReactNode, React.ReactNode, A>, ...args: A): React.ReactElement {
	if (isObject(el.props)) {
		const input = el.props.children as React.ReactNode;
		const output = transformNodes(input, transform, ...args);
		if (input !== output) return { ...el, props: { ...el.props, children: output } };
	}
	return el;
}

/**
 * Wrap unwrapped text with an element.
 * - If the node is empty (`null` or `undefined`) returns `null` (with no wrapping).
 * - If the node is an element returns the element (with no wrapping).
 * - If the node is a simple string or number or iterable node returns the node wrapped in `wrapper`
 */
export function wrapText(node: Exclude<React.ReactNode, null | undefined>, wrapper: React.ReactElement): React.ReactElement;
export function wrapText(node: React.ReactNode, wrapper: React.ReactElement): React.ReactElement | null;
export function wrapText<P>(node: React.ReactElement<P> | React.ReactNode, wrapper: React.ReactElement<P>): React.ReactElement<P> | null {
	return isNullish(node) ? null : isElement(node) ? node : { ...wrapper, props: { ...wrapper.props, children: node } };
}

/** Check a node and see if it needs to be swapped with an alternative. */
const _swapPlaceholder = (node: React.ReactNode, replacement: React.ReactNode): React.ReactNode => (node === PLACEHOLDER ? replacement : node);

/** Placeholder constant that indicates where to insert the node in `wrapNode()` and `<Wrap>` */
export const PLACEHOLDER = <div>PLACEHOLDER</div>;

/**
 * Wrap a node in an element.
 *
 * @param node The node to be wrapped.
 * - If `node` is `undefined` or `null` then `null` will be returned.
 * @param wrapper Node to wrap around `node`
 * - The `PLACEHOLDER` constant must be included in `wrapper` to indicate where `node` should be inserted.
 */
export function wrapNode(node: Exclude<React.ReactNode, null | undefined>, wrapper: React.ReactElement): React.ReactElement;
export function wrapNode(node: React.ReactNode, wrapper: React.ReactElement): React.ReactElement | null;
export function wrapNode(node: React.ReactNode, wrapper: React.ReactElement): React.ReactElement | null {
	return notOptional(node) ? transformChildren(wrapper, _swapPlaceholder, node) : null;
}

/**
 * Wrap children in an element.
 * - Basically `wrapNode()` but as a React element.
 */
export function Wrap({ wrapper, children }: { wrapper: React.ReactElement; children: React.ReactNode }): React.ReactElement | null {
	return wrapNode(children, wrapper);
}
